import { useEffect, useState } from 'react';

import { consultationService } from '../configs/api/api.config';

export type NetworkCheckState = 'pending' | 'ready' | 'failed';

interface NetworkCheckResponse {
  is_vpn: boolean | null;
  is_proxy: boolean | null;
  is_tor: boolean | null;
  is_datacenter: boolean | null;
  is_suspicious_network: boolean;
  source: string | null;
}

export function useVpnDetection(livekitToken: string | null, preflightState: NetworkCheckState) {
  const [result, setResult] = useState<NetworkCheckResponse | null>(null);
  const [isChecking, setIsChecking] = useState(preflightState === 'pending');
  const [isUnavailable, setIsUnavailable] = useState(preflightState === 'failed');

  useEffect(() => {
    if (preflightState === 'failed') {
      setIsChecking(false);
      setIsUnavailable(true);
      return;
    }
    if (!livekitToken) {
      setIsChecking(true);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5_000);
    setIsChecking(true);
    setIsUnavailable(false);

    fetch(`${consultationService}/consultations/network-check`, {
      method: 'POST',
      signal: controller.signal,
      headers: { Authorization: `Bearer ${livekitToken}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Network check failed');
        return (await response.json()) as NetworkCheckResponse;
      })
      .then((value) => {
        if (!controller.signal.aborted) setResult(value);
      })
      .catch(() => {
        if (!controller.signal.aborted) setIsUnavailable(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsChecking(false);
      });

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [livekitToken, preflightState]);

  return {
    isVpn: result?.is_suspicious_network === true,
    isChecking,
    isUnavailable,
    flags: result
      ? {
          is_vpn: result.is_vpn,
          is_proxy: result.is_proxy,
          is_tor: result.is_tor,
          is_datacenter: result.is_datacenter,
          source: result.source,
        }
      : null,
  };
}
